import { ConnectionOptions, GraphApiMethod } from '@/api/core';
import {
  DispatchIssueWithCache,
  MutationUpdateDispatchIssueBulkArgs,
  UpdateDispatchIssueBulkResponse,
  UpdateDispatchIssueInput,
  UpdateDispatchIssueValuesInput,
} from '@/models/gen/graphql';
import createGraphApiHook, { CreateGraphApiHookHook } from '@/hooks/createGraphApiHook';
import { getProperty, queryInput } from '@/utils';

import { TypedDocumentNode } from '@apollo/client';
import { UpdateDispatchIssue } from '@/features/IssueHistory/api/queries/UpdateDispatchIssue';
import graphApi from '@/api';

// Service Function Signature
type ServiceMethodPayload = {
  [key in keyof DispatchIssueWithCache]?: any;
}[];
interface ServiceMethod {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<UpdateDispatchIssueBulkResponse>;
}

// UpdateDispatchIssue Service
export const { updateDispatchIssue, useUpdateDispatchIssue } = ((): {
  updateDispatchIssue: ServiceMethod;
  useUpdateDispatchIssue: CreateGraphApiHookHook<ServiceMethod>;
} => {
  const [fetch] = graphApi(UpdateDispatchIssue).map(
    (fn: GraphApiMethod<TypedDocumentNode>): ServiceMethod =>
      async (
        payload?: Partial<UpdateDispatchIssueValuesInput & { id: string }>[],
        options?: ConnectionOptions
      ): Promise<UpdateDispatchIssueBulkResponse> => {
        debug.log('updateDispatchIssue,updateDispatchIssue.input', 'INPUT', payload);
        const updates: UpdateDispatchIssueInput[] =
          payload
            ?.filter(({ id }) => id)
            ?.map(
              ({ id, ...value }): UpdateDispatchIssueInput => ({
                query: [
                  {
                    id: queryInput(id),
                  },
                ],
                value: {
                  crewMemberGiftCardCount: value?.crewMemberGiftCardCount || [0],
                  crewMemberName: value?.crewMemberName || [''],
                  crewMemberPhone: value?.crewMemberPhone || [''],
                  datetime: value?.datetime || null,
                  dispatcherId: value?.dispatcherId || null,
                  driverId: value?.driverId || null,
                  driverPhone: value?.driverPhone || [''],
                  flightIssueDescription: value?.flightIssueDescription || null,
                  flightLate: value?.flightLate || null,
                  managerId: value?.managerId || null,
                  outboundFlightInfo: value?.outboundFlightInfo || null,
                  recoveryCompleted: value?.recoveryCompleted || null,
                  recoveryFlightDatetime: value?.recoveryFlightDatetime || null,
                  recoveryFlightNumber: value?.recoveryFlightNumber || null,
                  recoveryNeeded: value?.recoveryNeeded || null,
                  rootCause: value?.rootCause || null,
                  rootCauseDescription: value?.rootCauseDescription || null,
                  station: value?.station || null,
                  taxiCost: value?.taxiCost || [0],
                  taxiDetails: value?.taxiDetails || null,
                  taxiUsed: value?.taxiUsed || null,
                  tripId: value?.tripId || null,
                  flightType: value?.flightType || null,
                  pushTime: value?.pushTime || null,
                  recoveryFlightName: value?.recoveryFlightName || null,
                  scheduledTime: value?.scheduledTime || null,
                  vehicleId: value?.vehicleId || null,
                },
              })
            ) ?? [];
        const args: MutationUpdateDispatchIssueBulkArgs = {
          input: {
            updates,
          },
        };
        debug.log('updateDispatchIssue,updateDispatchIssue.payload', 'PAYLOAD', args);
        const opts = { merge: options?.merge ? 'updateDispatchIssueBulk' : undefined };
        const res = await fn(args, opts);
        debug.log('updateDispatchIssue,updateDispatchIssue.response', 'RESPONSE', res);
        return getProperty('updateDispatchIssueBulk', res);
      }
  );
  const useService = createGraphApiHook(fetch);
  return {
    updateDispatchIssue: fetch,
    useUpdateDispatchIssue: useService,
  };
})();
